/* eslint-disable max-len */
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { Notification, NotificationLevel } from 'src/app/model/notification';
import { SoftwareUpdatesService } from 'src/app/modules/software-updates/services/software-updates.service';
import { AppState } from 'src/app/store/app.reducer';
import * as NotificationsActions from '../notifications/notifications.actions';
import * as SoftwareUpdatesActions from './software-updates.actions';

/** Estrae un messaggio d'errore leggibile dalla HttpErrorResponse. */
const errorMessageOf = (error: unknown): string => {
	const err = error as { error?: { message?: string } | string; message?: string };
	if (err?.error && typeof err.error === 'object' && err.error.message) {
		return err.error.message;
	}
	if (typeof err?.error === 'string') {
		return err.error;
	}
	return err?.message ?? '';
};

@Injectable()
export class SoftwareUpdatesEffects {

	// --- Product ---
	getProducts$ = createEffect(() => this.actions$.pipe(
		ofType(SoftwareUpdatesActions.getProducts),
		switchMap(({ name, types }) => this.softwareUpdatesService.getProducts({ name, types }).pipe(
			map(resp => SoftwareUpdatesActions.getProductsSuccess({ productsResponseData: resp })),
			catchError(error => of(SoftwareUpdatesActions.getProductsFailed({ errorMessage: errorMessageOf(error) }))),
		)),
	));

	getProduct$ = createEffect(() => this.actions$.pipe(
		ofType(SoftwareUpdatesActions.getProduct),
		switchMap(({ productId }) => this.softwareUpdatesService.getProduct(productId).pipe(
			map((response) => SoftwareUpdatesActions.getProductSuccess({ product: response.data! })),
			catchError(error => of(SoftwareUpdatesActions.getProductFailed({ errorMessage: errorMessageOf(error) }))),
		)),
	));

	createProduct$ = createEffect(() => this.actions$.pipe(
		ofType(SoftwareUpdatesActions.createProduct),
		mergeMap(({ body }) => this.softwareUpdatesService.createProduct(body).pipe(
			map((response) => SoftwareUpdatesActions.createProductSuccess({ product: response.data! })),
			catchError(error => of(SoftwareUpdatesActions.createProductFailed({ errorMessage: errorMessageOf(error) }))),
		)),
	));

	updateProduct$ = createEffect(() => this.actions$.pipe(
		ofType(SoftwareUpdatesActions.updateProduct),
		mergeMap(({ productId, body }) => this.softwareUpdatesService.updateProduct(productId, body).pipe(
			map((response) => SoftwareUpdatesActions.updateProductSuccess({ product: response.data! })),
			catchError(error => of(SoftwareUpdatesActions.updateProductFailed({ errorMessage: errorMessageOf(error) }))),
		)),
	));

	deleteProduct$ = createEffect(() => this.actions$.pipe(
		ofType(SoftwareUpdatesActions.deleteProduct),
		mergeMap(({ productId }) => this.softwareUpdatesService.deleteProduct(productId).pipe(
			map(() => SoftwareUpdatesActions.deleteProductSuccess({ productId })),
			catchError(error => of(SoftwareUpdatesActions.deleteProductFailed({ errorMessage: errorMessageOf(error) }))),
		)),
	));

	reloadProducts$ = createEffect(() => this.actions$.pipe(
		ofType(
			SoftwareUpdatesActions.createProductSuccess,
			SoftwareUpdatesActions.updateProductSuccess,
			SoftwareUpdatesActions.deleteProductSuccess,
		),
		map(() => SoftwareUpdatesActions.getProducts({})),
	));

	// --- Hardware ---
	getHardwareList$ = createEffect(() => this.actions$.pipe(
		ofType(SoftwareUpdatesActions.getHardwareList),
		mergeMap(({ productId }) => this.softwareUpdatesService.getHardwareList(productId).pipe(
			map((response) => SoftwareUpdatesActions.getHardwareListSuccess({ productId, hardware: response.data ?? [] })),
			catchError(error => of(SoftwareUpdatesActions.getHardwareListFailed({ productId, errorMessage: errorMessageOf(error) }))),
		)),
	));

	createHardware$ = createEffect(() => this.actions$.pipe(
		ofType(SoftwareUpdatesActions.createHardware),
		mergeMap(({ productId, body }) => this.softwareUpdatesService.createHardware(productId, body).pipe(
			map((response) => SoftwareUpdatesActions.createHardwareSuccess({ productId, hardware: response.data! })),
			catchError(error => of(SoftwareUpdatesActions.createHardwareFailed({ errorMessage: errorMessageOf(error) }))),
		)),
	));

	updateHardware$ = createEffect(() => this.actions$.pipe(
		ofType(SoftwareUpdatesActions.updateHardware),
		mergeMap(({ productId, hardwareId, body }) => this.softwareUpdatesService.updateHardware(hardwareId, body).pipe(
			map((response) => SoftwareUpdatesActions.updateHardwareSuccess({ productId, hardware: response.data! })),
			catchError(error => of(SoftwareUpdatesActions.updateHardwareFailed({ errorMessage: errorMessageOf(error) }))),
		)),
	));

	deleteHardware$ = createEffect(() => this.actions$.pipe(
		ofType(SoftwareUpdatesActions.deleteHardware),
		mergeMap(({ productId, hardwareId }) => this.softwareUpdatesService.deleteHardware(hardwareId).pipe(
			map(() => SoftwareUpdatesActions.deleteHardwareSuccess({ productId, hardwareId })),
			catchError(error => of(SoftwareUpdatesActions.deleteHardwareFailed({ errorMessage: errorMessageOf(error) }))),
		)),
	));

	reloadHardware$ = createEffect(() => this.actions$.pipe(
		ofType(
			SoftwareUpdatesActions.createHardwareSuccess,
			SoftwareUpdatesActions.updateHardwareSuccess,
			SoftwareUpdatesActions.deleteHardwareSuccess,
		),
		map(({ productId }) => SoftwareUpdatesActions.getHardwareList({ productId })),
	));

	// --- Component ---
	getComponents$ = createEffect(() => this.actions$.pipe(
		ofType(SoftwareUpdatesActions.getComponents),
		mergeMap(({ hardwareId }) => this.softwareUpdatesService.getComponents(hardwareId).pipe(
			map((response) => SoftwareUpdatesActions.getComponentsSuccess({ hardwareId, components: response.data ?? [] })),
			catchError(error => of(SoftwareUpdatesActions.getComponentsFailed({ hardwareId, errorMessage: errorMessageOf(error) }))),
		)),
	));

	createComponent$ = createEffect(() => this.actions$.pipe(
		ofType(SoftwareUpdatesActions.createComponent),
		mergeMap(({ hardwareId, body }) => this.softwareUpdatesService.createComponent(hardwareId, body).pipe(
			map((response) => SoftwareUpdatesActions.createComponentSuccess({ hardwareId, component: response.data! })),
			catchError(error => of(SoftwareUpdatesActions.createComponentFailed({ errorMessage: errorMessageOf(error) }))),
		)),
	));

	updateComponent$ = createEffect(() => this.actions$.pipe(
		ofType(SoftwareUpdatesActions.updateComponent),
		mergeMap(({ hardwareId, componentId, body }) => this.softwareUpdatesService.updateComponent(componentId, body).pipe(
			map((response) => SoftwareUpdatesActions.updateComponentSuccess({ hardwareId, component: response.data! })),
			catchError(error => of(SoftwareUpdatesActions.updateComponentFailed({ errorMessage: errorMessageOf(error) }))),
		)),
	));

	deleteComponent$ = createEffect(() => this.actions$.pipe(
		ofType(SoftwareUpdatesActions.deleteComponent),
		mergeMap(({ hardwareId, componentId }) => this.softwareUpdatesService.deleteComponent(componentId).pipe(
			map(() => SoftwareUpdatesActions.deleteComponentSuccess({ hardwareId, componentId })),
			catchError(error => of(SoftwareUpdatesActions.deleteComponentFailed({ errorMessage: errorMessageOf(error) }))),
		)),
	));

	reloadComponents$ = createEffect(() => this.actions$.pipe(
		ofType(
			SoftwareUpdatesActions.createComponentSuccess,
			SoftwareUpdatesActions.updateComponentSuccess,
			SoftwareUpdatesActions.deleteComponentSuccess,
		),
		map(({ hardwareId }) => SoftwareUpdatesActions.getComponents({ hardwareId })),
	));

	// --- Notifiche errori ---
	notifyError$ = createEffect(() => this.actions$.pipe(
		ofType(
			SoftwareUpdatesActions.getProductsFailed,
			SoftwareUpdatesActions.getProductFailed,
			SoftwareUpdatesActions.createProductFailed,
			SoftwareUpdatesActions.updateProductFailed,
			SoftwareUpdatesActions.deleteProductFailed,
			SoftwareUpdatesActions.getHardwareListFailed,
			SoftwareUpdatesActions.createHardwareFailed,
			SoftwareUpdatesActions.updateHardwareFailed,
			SoftwareUpdatesActions.deleteHardwareFailed,
			SoftwareUpdatesActions.getComponentsFailed,
			SoftwareUpdatesActions.createComponentFailed,
			SoftwareUpdatesActions.updateComponentFailed,
			SoftwareUpdatesActions.deleteComponentFailed,
		),
		tap((action) => {
			const summary = this.translate.instant('GENERIC_ERROR');
			const message = action.errorMessage || summary;
			const notification = new Notification(summary, message, NotificationLevel.Error);
			this.store.dispatch(NotificationsActions.addNotification({ notification, toastOnly: true }));
		}),
	), { dispatch: false });

	constructor(
		private actions$: Actions,
		private softwareUpdatesService: SoftwareUpdatesService,
		private translate: TranslateService,
		private store: Store<AppState>,
	) { }
}
